import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["sidebar", "panel", "sectionTrigger", "sectionPanel"]
  static values = {
    defaultSection: String,
    initialOpen: Boolean,
  }

  connect() {
    this.handlePanelClick = this.handlePanelClick.bind(this)
    this.handleScroll = this.handleScroll.bind(this)
    this.routeSectionId = this.currentRouteSectionId()
    this.directSectionActive = this.hasActiveDirectSection()
    this.sidebarTarget.addEventListener("click", this.handlePanelClick)
    this.syncInitialSection()
    this.syncInitialOpen()
    this.syncBodyLock()
    this.handleViewportChange = this.handleViewportChange.bind(this)
    window.addEventListener("resize", this.handleViewportChange)
    window.addEventListener("scroll", this.handleScroll, { passive: true })
  }

  disconnect() {
    this.sidebarTarget.removeEventListener("click", this.handlePanelClick)
    window.removeEventListener("resize", this.handleViewportChange)
    window.removeEventListener("scroll", this.handleScroll)
    document.body.classList.remove("app-sidebar-mobile-open")
    this.element.classList.remove("is-sidebar-direct-hover")
  }

  openDesktop() {
    if (!this.isDesktop()) return

    this.element.classList.remove("is-sidebar-closing")
    this.syncPanelPosition()
    this.element.classList.add("is-sidebar-open")
  }

  closeDesktop() {
    if (!this.isDesktop()) return
    this.element.classList.remove("is-sidebar-closing")
    if (this.initialOpenValue) return

    this.element.classList.remove("is-sidebar-open")
    this.restoreRouteSection()
  }

  handleFocusOut() {
    if (!this.isDesktop()) return

    requestAnimationFrame(() => {
      if (!this.sidebarTarget.contains(document.activeElement)) {
        this.closeDesktop()
      }
    })
  }

  toggleMobile(event) {
    event.preventDefault()
    if (this.isDesktop()) return

    this.element.classList.toggle("is-sidebar-open")
    this.syncBodyLock()
  }

  closeMobile(event) {
    if (event) event.preventDefault()
    if (this.isDesktop()) return

    this.element.classList.remove("is-sidebar-open")
    this.syncBodyLock()
  }

  handlePanelClick(event) {
    const panelLink = event.target.closest(".app-sidebar__panel-link")
    if (!panelLink || !this.sidebarTarget.contains(panelLink)) return

    this.closeAfterPanelAction()
  }

  activateSection(event) {
    this.releaseDirectLinkPanel()

    const sectionId = event.currentTarget.dataset.sidebarSection
    if (!sectionId) return

    this.setActiveSection(sectionId)
    this.syncPanelPosition(event.currentTarget)

    if (this.isDesktop()) {
      this.openDesktop()
    }
  }

  handleDirectLinkClick() {
    this.releaseDirectLinkPanel()

    if (this.isDesktop()) return

    this.clearActiveSection()
    this.element.classList.remove("is-sidebar-open")
    this.syncBodyLock()
  }

  closeAfterPanelAction() {
    this.element.classList.remove("is-sidebar-open")
    this.element.classList.remove("is-sidebar-direct-hover")

    if (this.isDesktop()) {
      this.element.classList.add("is-sidebar-closing")
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur()
      }
    } else {
      this.syncBodyLock()
    }
  }

  syncInitialSection() {
    if (this.directSectionActive && !this.routeSectionId) {
      this.clearActiveSection()
      return
    }

    const initialSection = this.routeSectionId || this.defaultSectionValue || this.sectionTriggerTargets[0]?.dataset.sidebarSection

    if (initialSection) {
      this.setActiveSection(initialSection)
    }
  }

  syncInitialOpen() {
    if (!this.initialOpenValue || !this.isDesktop()) return

    this.openDesktop()
  }

  setActiveSection(sectionId) {
    this.sectionTriggerTargets.forEach((trigger) => {
      const isActive = trigger.dataset.sidebarSection === sectionId
      trigger.classList.toggle("is-active", isActive)
      trigger.setAttribute("aria-pressed", isActive ? "true" : "false")
      if (isActive) this.activeTrigger = trigger
    })

    this.sectionPanelTargets.forEach((panel) => {
      const isActive = panel.dataset.sidebarSectionPanel === sectionId
      panel.classList.toggle("is-active", isActive)
      panel.hidden = !isActive
    })
  }

  restoreRouteSection() {
    if (this.directSectionActive && !this.routeSectionId) {
      this.clearActiveSection()
      return
    }

    const sectionId = this.routeSectionId || this.defaultSectionValue || this.sectionTriggerTargets[0]?.dataset.sidebarSection
    if (!sectionId) return

    this.setActiveSection(sectionId)
  }

  currentRouteSectionId() {
    return this.sectionTriggerTargets.find((trigger) => trigger.classList.contains("is-active"))?.dataset.sidebarSection
  }

  hasActiveDirectSection() {
    return Array.from(this.sidebarTarget.querySelectorAll(".app-sidebar__section-trigger.is-active")).some((trigger) => {
      return !trigger.dataset.sidebarSection
    })
  }

  clearActiveSection() {
    this.sectionTriggerTargets.forEach((trigger) => {
      trigger.classList.remove("is-active")
      trigger.setAttribute("aria-pressed", "false")
    })

    this.sectionPanelTargets.forEach((panel) => {
      panel.classList.remove("is-active")
      panel.hidden = true
    })
  }

  suppressPanelForDirectLink() {
    if (!this.isDesktop()) return

    this.element.classList.remove("is-sidebar-open")
    this.element.classList.add("is-sidebar-direct-hover")
  }

  releaseDirectLinkPanel() {
    this.element.classList.remove("is-sidebar-direct-hover")
  }

  syncPanelPosition() {
    if (!this.isDesktop() || !this.hasPanelTarget) return

    const sidebarRect = this.sidebarTarget.getBoundingClientRect()
    const panelTop = Math.max(0, -sidebarRect.top)
    const availableHeight = Math.max(240, window.innerHeight)

    this.panelTarget.style.setProperty("--app-sidebar-panel-top", `${panelTop}px`)
    this.panelTarget.style.setProperty("--app-sidebar-panel-max-height", `${availableHeight}px`)
  }

  currentActiveTrigger() {
    return this.sectionTriggerTargets.find((trigger) => trigger.classList.contains("is-active"))
  }

  isDesktop() {
    return window.matchMedia("(min-width: 992px)").matches
  }

  syncBodyLock() {
    document.body.classList.toggle("app-sidebar-mobile-open", !this.isDesktop() && this.element.classList.contains("is-sidebar-open"))
  }

  handleViewportChange() {
    if (this.isDesktop()) {
      document.body.classList.remove("app-sidebar-mobile-open")
      this.element.classList.remove("is-sidebar-closing")
      this.element.classList.remove("is-sidebar-direct-hover")
      this.syncPanelPosition()
      if (this.initialOpenValue) {
        this.openDesktop()
      }
    } else {
      this.panelTarget?.style.removeProperty("--app-sidebar-panel-top")
      this.panelTarget?.style.removeProperty("--app-sidebar-panel-max-height")
      this.syncBodyLock()
    }
  }

  handleScroll() {
    if (!this.isDesktop() || !this.element.classList.contains("is-sidebar-open")) return

    this.syncPanelPosition()
  }
}
